import { TextField, Select, Checkbox } from '@/components/design-system'
import {
  FLEET_MACHINE_PROVIDERS,
  FLEET_MACHINE_ENVIRONMENTS,
  FLEET_MACHINE_CONTROL_MODES,
  FLEET_MACHINE_PROVIDER_LABELS,
  FLEET_MACHINE_ENVIRONMENT_LABELS,
  FLEET_MACHINE_CONTROL_MODE_LABELS,
} from '@/frontend/types/fleetMachine'

export type FleetMachineFormData = {
  name: string
  slug: string
  provider: string
  environment: string
  control_mode: string
  api_base_url: string
  instance_url_template: string
  enabled: string
  metadata: string
  generate_telemetry_token: string
  generate_api_token: string
}

export type FleetMachineFormErrors = Partial<Record<keyof FleetMachineFormData, string>>

interface Props {
  data: FleetMachineFormData
  errors: FleetMachineFormErrors
  setData: (key: keyof FleetMachineFormData, value: string) => void
  mode: 'create' | 'edit'
  hasTelemetryToken: boolean
  onRotateTelemetryToken?: () => void
  hasApiToken: boolean
  onRotateApiToken?: () => void
}

const providerOptions = FLEET_MACHINE_PROVIDERS.map((v) => ({ label: FLEET_MACHINE_PROVIDER_LABELS[v], value: v }))
const environmentOptions = FLEET_MACHINE_ENVIRONMENTS.map((v) => ({ label: FLEET_MACHINE_ENVIRONMENT_LABELS[v], value: v }))
const controlModeOptions = FLEET_MACHINE_CONTROL_MODES.map((v) => ({ label: FLEET_MACHINE_CONTROL_MODE_LABELS[v], value: v }))

export default function FleetMachineFormFields({ data, errors, setData, mode, hasTelemetryToken, onRotateTelemetryToken, hasApiToken, onRotateApiToken }: Props) {
  const isFleetApiMode = data.control_mode === 'fleet_api'
  return (
    <div className="space-y-4">
      <TextField
        label="Nome"
        value={data.name}
        onChange={(value) => setData('name', value)}
        error={errors.name}
        placeholder="Ex.: Produção SP"
      />
      <TextField
        label="Slug"
        value={data.slug}
        onChange={(value) => setData('slug', value)}
        error={errors.slug}
        placeholder="Opcional — gerado a partir do nome"
        helpText="Apenas letras minúsculas, números e hífens."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Select
          label="Provedor"
          options={providerOptions}
          value={data.provider}
          onChange={(value) => setData('provider', value)}
          error={errors.provider}
        />
        <Select
          label="Ambiente"
          options={environmentOptions}
          value={data.environment}
          onChange={(value) => setData('environment', value)}
          error={errors.environment}
        />
        <Select
          label="Modo de controle"
          options={controlModeOptions}
          value={data.control_mode}
          onChange={(value) => setData('control_mode', value)}
          error={errors.control_mode}
        />
      </div>
      <TextField
        label="API base URL"
        value={data.api_base_url}
        onChange={(value) => setData('api_base_url', value)}
        error={errors.api_base_url}
        placeholder="https://fleet.example.com"
        helpText="Opcional. Usado apenas quando o modo de controle for Fleet API."
      />
      <TextField
        label="Template de URL da instância"
        value={data.instance_url_template}
        onChange={(value) => setData('instance_url_template', value)}
        error={errors.instance_url_template}
        placeholder="https://{{instance_name}}.example.com"
        helpText="Define o api_endpoint_url do agente. Placeholders suportados: {{instance_name}}, {{org_slug}}."
      />
      <TextField
        label="Metadata (JSON)"
        value={data.metadata}
        onChange={(value) => setData('metadata', value)}
        error={errors.metadata}
        multiline={4}
        placeholder="{}"
        helpText="JSON opcional com metadados arbitrários."
      />
      <Checkbox
        label="Habilitada"
        checked={data.enabled === 'true'}
        onChange={(checked) => setData('enabled', checked ? 'true' : 'false')}
        helpText="Máquinas desabilitadas não aparecem para atribuição a organizações."
      />
      {mode === 'create' ? (
        <>
          <Checkbox
            label="Gerar token de telemetria"
            checked={data.generate_telemetry_token === 'true'}
            onChange={(checked) => setData('generate_telemetry_token', checked ? 'true' : 'false')}
            helpText="O token será exibido uma única vez após a criação."
          />
          {isFleetApiMode && (
            <Checkbox
              label="Gerar token da API"
              checked={data.generate_api_token === 'true'}
              onChange={(checked) => setData('generate_api_token', checked ? 'true' : 'false')}
              helpText="O token será exibido uma única vez após a criação."
            />
          )}
        </>
      ) : (
        <>
          <div className="rounded-md border border-border p-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-foreground">Token de telemetria</div>
              <div className="text-xs text-muted-foreground">
                {hasTelemetryToken
                  ? 'Um token está configurado. Rotacionar substitui o atual.'
                  : 'Nenhum token configurado ainda.'}
              </div>
            </div>
            {onRotateTelemetryToken && (
              <button
                type="button"
                onClick={onRotateTelemetryToken}
                className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent transition-colors"
              >
                {hasTelemetryToken ? 'Rotacionar' : 'Gerar'}
              </button>
            )}
          </div>
          {isFleetApiMode && (
            <div className="rounded-md border border-border p-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-foreground">Token da API</div>
                <div className="text-xs text-muted-foreground">
                  {hasApiToken
                    ? 'Um token está configurado. Rotacionar substitui o atual.'
                    : 'Nenhum token configurado ainda.'}
                </div>
              </div>
              {onRotateApiToken && (
                <button
                  type="button"
                  onClick={onRotateApiToken}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent transition-colors"
                >
                  {hasApiToken ? 'Rotacionar' : 'Gerar'}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export function blankFleetMachineFormData(): FleetMachineFormData {
  return {
    name: '',
    slug: '',
    provider: 'aws_ec2',
    environment: 'production',
    control_mode: 'legacy_ssh',
    api_base_url: '',
    instance_url_template: '',
    enabled: 'true',
    metadata: '',
    generate_telemetry_token: 'false',
    generate_api_token: 'false',
  }
}
